import {createFeatureSelector, createSelector} from '@ngrx/store';
import {
	selectActiveSymbolsVisibility,
	selectByList,
	selectCitizenId,
	selectHiddenSchemaIds,
	selectJournalState
} from './journal.selectors';
import * as fromTemplateFields from '../_selectors/template-field-entity.selectors';
import {adapter, featureKey, State} from '../_reducers/entry-entity.reducer';
import {sortEntriesList} from '../_reducers/sortEntriesList.fn';
import {calendarOverlayFromEntries} from '../_reducers/calendarOverlayFromEntries.fn';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import {EDIT_DRAWING_PATH, EMPTY_PATH, JOURNAL_PATH, PREVIEW_PATH, VIEW_PATH} from 'shared/magic';
import {WidgetsEnum} from '../_interface/widgets.enum';
import {IFullTemplateField} from '../_interface/itemplate-field';
import {IEntryFieldForShow} from '../_interface/ientry-field-for-show';
import {IEntryForShow} from '../_interface/ientry-for-show';
import {FieldTypes} from '../_interface/field-types.enum';
import * as fieldTypes from './field-type-entity.selectors';
import {JournalSymbolField} from '../_interface/journal-symbol-field.interface';
import {convertFieldToPlacedSymbol} from '../lib/drawing/_functions/converFieldToSymbol.fn';
import * as symbolsSelectors from '../lib/drawing/_selectors/symbol.selectors';
import * as schemaSelectors from '../lib/drawing/_selectors/schema.selectors';
import {convertEntryFieldFromShowToManipulation} from '../_reducers/convertEntryForEdit.fn';
import {DrawSchemaWithSymbols} from '../lib/drawing/_interface/schema.model';
import {DrawSymbolPlaced} from '../lib/drawing/_interface/current-symbol-drag.interface';
import {prepareEntriesByDrawings} from '../_reducers/prepareEntriesByDrawings.fn';
import {selectAllAuthors} from '../lib/journal/select-filter-author/fn/selectAllAuthors.fn';
import { findDiagnosisEnum, findMedicineEnum, findPulseEnum, hasFieldValue, matchesDiagnosisStatus, searchInObject } from '../lib/journal/search-bar/searchInObject.fn';
import { selectEntities as selectDiagnosisEntities } from './diagnosis.selectors';
import { schemaEntityState } from '../lib/drawing/_selectors/schema.selectors';

dayjs.extend(isBetween);

export const entityState = createFeatureSelector<State>(featureKey);
export const selectLoaded = createSelector(entityState, state => state.loaded);
export const selectAll = createSelector(entityState, adapter.getSelectors().selectAll);
export const selectEntities = createSelector(entityState, adapter.getSelectors().selectEntities);
export const selectEntriesByList = createSelector(
	selectAll,
	selectByList,
	(entries, {sortBy, direction}) => sortEntriesList(entries, sortBy, direction)
);

export const selectAuthorsFromEntries = createSelector(
	selectAll,
	(entries) => {
		return selectAllAuthors(entries);
	}
);

export const selectTemplateFieldsWithWarning = createSelector(
	fromTemplateFields.selectEntities, (fullTemplateFields) => {
		const templateFields = Object.values(fullTemplateFields);
		const warningTemplateFields = templateFields.filter((tf) => tf.body?.isWarning);
		return warningTemplateFields || [];
	}
);

export const selectEntriesByWarning = createSelector(
	selectAll,
	selectTemplateFieldsWithWarning,
	(entries, warningTemplateFields) => {
		if (!warningTemplateFields.length) return [];
		const warningIds = warningTemplateFields.map(({templateFieldId}) => templateFieldId);
		const entriesWithWarnings = entries.filter(({fields}) => fields.find(({templateFieldId}) => warningIds.includes(templateFieldId)));
		return entriesWithWarnings || [];
	}
);

export const selectFieldsWithWarnings = createSelector(
	selectAll,
	fromTemplateFields.selectEntities,
	(entries, templateFieldsDict): Array<{ entry: IEntryForShow, field: IEntryFieldForShow, templateField: IFullTemplateField }> => {
		const entriesWithWarnings = entries.filter(({fields}) => fields.find((field) => field.content.lastEntry.isWarning));
		return entriesWithWarnings.reduce((acc, entry) => {
			return [
				...acc,
				...entry.fields
					.map(field => {
						return {
							entry,
							field,
							templateField: templateFieldsDict[field.templateFieldId]
						};
					})
					.filter(({templateField}) => Boolean(templateField))
					.filter(({field, templateField: {fieldType}}) =>
						field?.content.lastEntry.isWarning || [FieldTypes.file].includes(fieldType)
					)
			];
		}, []).sort((a, b) => a.field.content.lastEntry.modificationDate < b.field.content.lastEntry.modificationDate ? 1 : -1);
	}
);

export const selectFieldsWithWarningsFromTemplate = createSelector(
	selectAll,
	fromTemplateFields.selectEntities,
	(entries, templateFieldsDict): Array<{ entry: IEntryForShow, field: IEntryFieldForShow, templateField: IFullTemplateField }> => {
		const templateFields = Object.values(templateFieldsDict);
		const warningTemplateFields = templateFields.filter((tf) => tf.body?.isWarning);
		if (!warningTemplateFields.length) return [];
		const warningIds = warningTemplateFields.map(({templateFieldId}) => templateFieldId);
		const entriesWithWarnings = entries.filter(({fields}) => fields.find(({templateFieldId}) => warningIds.includes(templateFieldId)));
		return entriesWithWarnings.reduce((acc, entry) => [
				...acc,
				...entry.fields
					.map(field => ({
						entry,
						field,
						templateField: templateFieldsDict[field.templateFieldId]
					}))
					.filter(({templateField: {body: {isWarning}}}) => isWarning)
					.filter(({templateField: {fieldType}}) => fieldType !== FieldTypes.richtext)
					.filter(({templateField: {fieldType}}) => fieldType !== FieldTypes.file)
			],
			[]).sort((a, b) => a.field.content.lastEntry.modificationDate < b.field.content.lastEntry.modificationDate ? 1 : -1);
	}
);

export const selectEntriesByWidget = (widgetType: WidgetsEnum) => createSelector(
	selectAll,
	fromTemplateFields.selectEntities,
	(entries, templateFieldsDict) => {
		const templateFields = Object.values(templateFieldsDict);
		const widgetTemplateFields = templateFields.filter((tf) => tf.body?.widgetType === widgetType);
		if (!widgetTemplateFields.length) return [];
		const widgetIds = widgetTemplateFields.map(({templateFieldId}) => templateFieldId);
		const entriesWithWidget = entries.filter(({fields}) => fields.find(({templateFieldId}) => widgetIds.includes(templateFieldId)));
		return entriesWithWidget || [];
	}
);

export const selectEntriesFieldsByWidget = (widgetType: WidgetsEnum) => createSelector(
	selectEntriesByWidget(widgetType),
	fromTemplateFields.selectEntities,
	(entries, templateFieldsDict): IEntryFieldForShow[] => {
		const templateFields = Object.values(templateFieldsDict);
		const widgetTemplateFields = templateFields.filter((tf) => tf.body?.widgetType === widgetType);
		if (!widgetTemplateFields.length) return [];
		const widgetIds = widgetTemplateFields.map(({templateFieldId}) => templateFieldId);
		const fields = entries.reduce((acc, e) => [...acc, ...e.fields], []);
		const fieldsWithWidget = fields.filter(({templateFieldId}) => widgetIds.includes(templateFieldId));
		return fieldsWithWidget || [];
	}
);

export const selectByDateOverlay = createSelector(
	selectAll, entries => calendarOverlayFromEntries(entries)
);

export const selectSingleEntryForPreview = (entryId: string) => createSelector(
	selectAll, (entries) => entries.find(acc => acc.entryId === entryId)
);

export const previewEntryPathSelector = (entryId: string) => createSelector(
	selectCitizenId, (citizenId) => {
		if (!citizenId || !entryId) return [];
		return [EMPTY_PATH, JOURNAL_PATH, VIEW_PATH, citizenId, PREVIEW_PATH, entryId];
	}
);

export const editDrawingPathSelector = (entryId?: string) => createSelector(
	selectCitizenId, (citizenId) => {
		if (!citizenId) return [];
		return entryId ?
			[EMPTY_PATH, JOURNAL_PATH, VIEW_PATH, citizenId, entryId, EDIT_DRAWING_PATH] :
			[EMPTY_PATH, JOURNAL_PATH, VIEW_PATH, citizenId, EDIT_DRAWING_PATH];

	}
);

export const selectSymbolFields = createSelector(
	fieldTypes.selectSymbolFieldType,
	symbolsSelectors.selectAllEntities,
	selectAll,
	(schemaFieldType, symbols, entries): JournalSymbolField[] => {
		if (!schemaFieldType) return null;
		const filterSymbol = (field: IEntryFieldForShow) => field.fieldTypeId === schemaFieldType.entryFieldTypeId;
		const filteredEntries = entries.filter(entry => entry.fields.find(filterSymbol));
		if (!filteredEntries.length) return [];
		return filteredEntries.reduce((acc, {fields, entryId}) => {
			const s: JournalSymbolField[] = fields.filter(filterSymbol).map(field => ({
				field, entryId,
				placedSymbolId: field.content.lastEntry.placedSymbolId,
				placedSymbol: convertFieldToPlacedSymbol(entryId, convertEntryFieldFromShowToManipulation(field), symbols),
				schemaId: field.content.lastEntry.schemaId,
				symbolId: field.content.lastEntry.symbolId
			}));
			return [...acc, ...s];
		}, []).filter(({schemaId, placedSymbolId, symbolId}) => schemaId && placedSymbolId && symbolId);
	}
);

export const selectHasEntriesDrawings = createSelector(
	selectSymbolFields,
	(symbolFields): boolean => {
		return !!symbolFields?.length;
	}
);

export const selectHasEntrySymbols = (entryId: string) => createSelector(
	selectSymbolFields,
	(sf): boolean => !!sf?.find((field) => field.entryId === entryId)
);

export const selectAllSymbols = createSelector(
	selectSymbolFields,
	symbolsSelectors.selectAllEntities,
	(journalFields, symbols): DrawSymbolPlaced[] => {
		return journalFields.map(({entryId, field}) =>
			convertFieldToPlacedSymbol(entryId, convertEntryFieldFromShowToManipulation(field), symbols));
	}
);

export const selectFilteredSymbols = ({forSchemaId, forEntryId}: {forSchemaId?: string, forEntryId?: string}) => createSelector(
	selectAllSymbols,
	selectActiveSymbolsVisibility,
	(symbols, showInactive) => {
		const filteredSymbols = symbols.filter(Boolean);
		const filterSchema = ({placedSymbol: {schemaId}}: DrawSymbolPlaced) => schemaId === forSchemaId;
		const filterEntry = ({entryId}: DrawSymbolPlaced) => entryId === forEntryId;
		const filterInactive = ({active}: DrawSymbolPlaced) => !!active !== showInactive;
		return filteredSymbols
			.filter(forSchemaId ? filterSchema : Boolean)
			.filter(forEntryId ? filterEntry : Boolean)
			.filter(showInactive ? Boolean : filterInactive);
	}
);

export const selectSymbolsByIds = (placedSymbolIds: string[]) => createSelector(
	selectSymbolFields,
	(symbolFields): JournalSymbolField[] =>
		symbolFields.filter((s) => placedSymbolIds.includes(s.placedSymbolId))
);

export const selectSymbolFieldsForSchema = (schemaId: string) => createSelector(
	selectSymbolFields,
	(symbolFields): JournalSymbolField[] => symbolFields.filter((s) => s.schemaId === schemaId)
);

export const selectSymbolsForSchemaAndEntry = (schemaId: string, entryId?: string) => createSelector(
	selectSymbolFieldsForSchema(schemaId),
	(symbols) => symbols.filter(symbol => entryId ? symbol.entryId === entryId : true)
);

export const selectSymbolsForEntry = (entryId: string) => createSelector(
	selectSymbolFields,
	(symbolFields) => symbolFields.filter((s) => s.entryId === entryId)
);

export const selectSchemaIdsFromEntryFields = createSelector(
	selectSymbolFields,
	(fields) => {
		if (!fields) return null;
		return Array.from(new Set(fields.map(({schemaId}) => schemaId)));
	}
);

export const selectFilteredSchemaIdsFromEntryFields = createSelector(
	selectSchemaIdsFromEntryFields,
	selectHiddenSchemaIds,
	(schemaIds, hiddenSchemaIds) => {
		return schemaIds.filter(schemaId => !hiddenSchemaIds.includes(schemaId));
	}
);

export const selectSymbolsForSchemaForSingleEntry = (schemaId: string, entryId: string) => createSelector(
	selectSymbolFields,
	(symbolFields) => symbolFields.filter((s) => s.schemaId === schemaId && s.entryId === entryId)
);

export const selectSchemasIdForEntry = (entryId: string) => createSelector(
	selectSymbolFields,
	(fields) => {
		if (!fields) return [];
		const entries = fields.filter((s) => s.entryId === entryId);
		return [...new Set(entries.map(({schemaId}) => schemaId))];
	}
);

export const selectSchemasWithSymbolsForEntry = (entryId: string) => createSelector(
	selectSymbolFields,
	schemaSelectors.schemaEntityState,
	selectActiveSymbolsVisibility,
	(fields, {entities}, showInactive): DrawSchemaWithSymbols[] => {
		if (!fields) return null;
		const entries = fields.filter((s) => s.entryId === entryId);
		const schemaIds = [...new Set(entries.map(({schemaId}) => schemaId))];
		const filterInactive = ({placedSymbol}: JournalSymbolField) => !!placedSymbol.active !== showInactive;
		return schemaIds.map(id => entities[id]).filter(Boolean).map((schema: DrawSchemaWithSymbols) => {
			const symbols = entries
				.filter(symbol => symbol.schemaId === schema.schemaId)
				.filter(showInactive ? Boolean : filterInactive);
			return {...schema, symbols};
		});
	}
);

export const selectEntriesByDrawings = createSelector(
	selectAll,
	selectEntities,
	selectSymbolFields,
	schemaSelectors.selectAllEntities,
	(entriesList, entriesDict, symbolFields, schemas) => {
		return prepareEntriesByDrawings(entriesList, entriesDict, symbolFields, schemas);
	}
);

export const selectDiagnosisFields = createSelector(
	selectEntriesFieldsByWidget(WidgetsEnum.diagnosis),
	fields => fields
);

export const selectDiagnosisIdsFromFields = createSelector(
	selectDiagnosisFields,
	(fields) => [...new Set(fields.map((f) => f.content.lastEntry.diagnosisId))]
);

export const selectSchemaEntities = createSelector(
	schemaEntityState,
	({ entities }) => entities
);

export const selectFilteredEntriesForShow = createSelector(
	selectAll,
	selectJournalState,
	selectDiagnosisEntities,
	selectSchemaEntities,
	(allEntries, journalState, diagnosisEntities, schemaEntities) => {
		const { selectedTemplateIds, searchKeyword, selectedAuthorIds, selectedDateRange } = journalState;
		const keyword = searchKeyword?.trim().toLowerCase();
		const shouldFilterByTemplate = !!selectedTemplateIds?.length;
		const shouldFilterByKeyword = !!keyword;
		const shouldFilterByAuthor = !!selectedAuthorIds?.length;
		const shouldFilterByDateRange = !!selectedDateRange?.from && !!selectedDateRange?.to;

		const sortDescending = (a: IEntryForShow, b: IEntryForShow) => (a.entryDateTime < b.entryDateTime ? 1 : -1);

		if (!shouldFilterByTemplate && !shouldFilterByKeyword && !shouldFilterByAuthor && !shouldFilterByDateRange) {
			return [...allEntries].sort(sortDescending);
		}

		return allEntries.filter(entry => {
			const checks: boolean[] = [];
			if (shouldFilterByTemplate) {
				checks.push(selectedTemplateIds.includes(entry.templateId));
			}
			if (shouldFilterByDateRange) {
				const { from, to } = selectedDateRange;
				const entryDate = dayjs(entry.entryDateTime);
				checks.push(entryDate.isBetween(dayjs(from), dayjs(to), null, '[]'));
			}
			if (shouldFilterByKeyword) {
					const matchesKeyword = entry.fields?.some(field => {
						const inLastEntry = searchInObject(field.content?.lastEntry, keyword);
						const inLabel = field.label?.toLowerCase().includes(keyword) && hasFieldValue(field);
						const inSymbol = field.label?.toLowerCase().includes(keyword) && field.content?.lastEntry?.symbolId;
						const matchedDiagnoses = Object.values(diagnosisEntities).filter(
							diagnosis =>
								diagnosis?.name?.toLowerCase().includes(keyword) ||
								diagnosis?.code?.toLowerCase().includes(keyword)
						);
						const inDiagnosis = matchedDiagnoses.some(
							diag => field.content?.lastEntry?.diagnosisId === diag.diagnosisId
						);
						let inDiagnosisStatus = false;
						if (field?.name?.toLowerCase() === 'diagnosis'){
							inDiagnosisStatus = matchesDiagnosisStatus(keyword, field.content?.lastEntry?.active);
						}						
						const inPulseLabels = !!findPulseEnum(keyword) && field.label?.toLowerCase().includes('pulse');
						const inMedicineLabels = !!findMedicineEnum(keyword) && field.label?.toLowerCase().includes('medicine');
						const inDiagnosisLabels = !!findDiagnosisEnum(keyword) && field.label?.toLowerCase().includes('diagnosis');
						const schemaIds: string[] = [
							field.content?.lastEntry?.schemaId,
							...(field.content?.history?.map(h => h.schemaId) ?? [])
						].filter(Boolean);
						let inSchema: boolean;
						const lowerKeyword = keyword.toLowerCase().trim();
						if (lowerKeyword.startsWith('drawing:') || lowerKeyword.startsWith('tegn:')) {
							const afterPrefix = lowerKeyword.replace(/^drawing:\s*/, '');
							const afterPrefixDanish = lowerKeyword.replace(/^tegn:\s*/, '');
							if (afterPrefix === '' || afterPrefixDanish=== '') {
								inSchema = schemaIds.length > 0;
							} else {
								inSchema = schemaIds.some(
									id => schemaEntities[id]?.name?.toLowerCase().includes(afterPrefix)
										|| schemaEntities[id]?.name?.toLowerCase().includes(afterPrefixDanish)
								);
							}
						} else if ('drawing'.startsWith(lowerKeyword) || 'tegn'.startsWith(lowerKeyword)) {
							inSchema = schemaIds.length > 0;
						} else {
							inSchema = schemaIds.some(
								id => schemaEntities[id]?.name?.toLowerCase().includes(lowerKeyword)
							);
						}						
						return inLastEntry || inLabel || inSymbol || inDiagnosis || inPulseLabels || inMedicineLabels || inDiagnosisLabels || inDiagnosisStatus || inSchema;
					});


					checks.push(matchesKeyword);
				}
			if (shouldFilterByAuthor) {
				checks.push(selectedAuthorIds.includes(entry.author.authorId));
			}

			return checks.every(Boolean);
		}).sort(sortDescending);
	});

